import React, { useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useDispatch } from 'react-redux';

import { useLogger, useSelector } from '~Hooks';
import * as actions from '~actions';
import { UniverseBuilder } from '@baemingo/device-settings-events';
import {
  AvailablePrinter,
  Printer,
  SerializedState,
} from '@baemingo/device-settings-events/dist/types';
import { useStorage } from '~Components/Storage';

type ContextType = SerializedState | null;

/**
 * Create a context with an empty universe. This context acts as a
 * state that we can access from child components. We use this in app.tsx
 */
// @ts-ignore
const Context = React.createContext<ContextType>(undefined);

// Specific context for updating the device ID.
// Device ID lives in Storage, which relies on the DeviceSettingsUniverseProvider,
// which creates a circular dependency. This is a workaround.
const UpdateContext = React.createContext<(deviceId: string | null) => void>(() => {
  throw new Error('UpdateContext must be used within a DeviceSettingsUniverseProvider');
});

export const DeviceSettingsUniverseProvider: React.FunctionComponent = ({ children }) => {
  const [deviceId, setDeviceId] = useState<string | null>(null);

  // A local universe
  const universe = useRef(new UniverseBuilder());

  // The current index of which event we've added to the universe from redux
  const index = useRef(0);

  // Get events from state. Events are added via UI actions
  const events = useSelector((state) => state.deviceSettingsEvents);

  // Get snapshot from state. If this changes we override our current universe
  const snapshot = useSelector((state) => state.deviceSettingsSnapshot.snapshot);

  // We save the serialized universe in state to trigger re-renders
  const [state, setState] = useState<ContextType>(null);

  const mountLogger = useLogger('mount');

  useEffect(() => {
    mountLogger.info('Mounted: DeviceSettingsUniverseProvider');
    return () => {
      mountLogger.info('Unmounted: DeviceSettingsUniverseProvider');
    };
  }, []);

  useEffect(() => {
    // Start by removing any events we've already added, then add the event
    events.slice(index.current).forEach((event) => universe.current.addEvent(event));

    // Update our active index
    index.current = events.length;

    // Save to state and trigger a re-render
    setState(universe.current.serialize());
  }, [events]);

  useEffect(() => {
    if (!deviceId) return;

    universe.current = new UniverseBuilder(snapshot!);
    setState(universe.current.serialize());
  }, [snapshot, deviceId]);

  return (
    <Context.Provider value={state}>
      <UpdateContext.Provider value={setDeviceId}>{children}</UpdateContext.Provider>
    </Context.Provider>
  );
};

export const useDeviceSettingsUniverse = () => {
  const value = useContext(Context);

  if (value === undefined) {
    throw new Error(
      'useDeviceSettingsUniverse must be used within a DeviceSettingsUniverseProvider',
    );
  }

  return value;
};

export const useUpdateDeviceId = () => {
  return useContext(UpdateContext);
};

/**
 * API methods for interacting with a device
 */
export const useDeviceSettingsApi = () => {
  const dispatch = useDispatch();
  const [storage] = useStorage();

  const generateEventMeta = () => ({
    createdAt: new Date().toISOString(),
    eventId: uuid(),
    sentAt: null,
    deviceId: storage.deviceId,
  });

  const API = {
    monitoring: {
      setStorageField(key: string, value: any) {
        /** Backend index requires deviceId - don't queue events without one */
        if (!storage.deviceId) {
          return;
        }

        dispatch(
          actions.addDeviceSettingsEvent({
            ...generateEventMeta(),
            type: 'UPDATE_STORAGE_MONITORING_KEY',
            key,
            value,
          }),
        );
      },
      setPrinters(printers: Printer[]) {
        dispatch(
          actions.addDeviceSettingsEvent({
            ...generateEventMeta(),
            type: 'SET_PRINTERS',
            printers,
          }),
        );
      },
      setAvailablePrinters(printers: AvailablePrinter[]) {
        dispatch(
          actions.addDeviceSettingsEvent({
            ...generateEventMeta(),
            type: 'SET_AVAILABLE_PRINTERS',
            printers,
          }),
        );
      },
    },
  };

  return API;
};
